import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  addDrive,
  addDriveRoot,
  browseDriveFolder,
  findLiveFiles,
  getCurrentDriveSyncRun,
  keepDriveFiles,
  listDriveMirroredRefs,
  listDriveSyncRuns,
  resyncDrive,
  syncDriveNow,
  applyDriveRoots,
  decideAnchorProposals,
  listAnchorProposals,
  reopenAnchorProposal,
  listAvailableDrives,
  listDrives,
  pinReference,
  readFileText,
  removeDrive,
  removeDriveRoot,
  updateDriveRoot,
  getPublishStatus,
  setPublishTarget,
  removePublishTarget,
  publishNow,
} from '@/api/drives-api';
import type { DriveKeepBody, DriveMirroredRef, DriveRootCreateBody, DriveRootUpdateBody } from '@vivport/backend/types';
import { contentKeys } from './useContentQueries';

export const drivesKeys = {
  all: ['drives'] as const,
  list: (memberId: number) => [...drivesKeys.all, 'list', memberId] as const,
  available: (memberId: number) => [...drivesKeys.all, 'available', memberId] as const,
  browse: (driveId: number, parent: string | null) => [...drivesKeys.all, 'browse', driveId, parent] as const,
  currentRun: (driveId: number) => [...drivesKeys.all, 'currentRun', driveId] as const,
  runs: (driveId: number) => [...drivesKeys.all, 'runs', driveId] as const,
  proposals: (rootId: number | 'all') => [...drivesKeys.all, 'proposals', rootId] as const,
  mirroredRefs: () => [...drivesKeys.all, 'mirrored-refs'] as const,
  mirroredRefsForDrive: (driveId: number) => [...drivesKeys.mirroredRefs(), driveId] as const,
  mirroredRefsQuery: (driveId: number, sortedPaths: string[]) =>
    [...drivesKeys.mirroredRefsForDrive(driveId), sortedPaths] as const,
  liveFind: (driveId: number, query: string) => [...drivesKeys.all, 'live-find', driveId, query] as const,
  publishStatus: () => [...drivesKeys.all, 'publish-status'] as const,
};

const PUBLISH_STATUS_POLL_IN_FLIGHT_MS = 5_000;
const PUBLISH_STATUS_POLL_IDLE_MS = 60_000;

export const DRIVE_LIVE_FIND_LIMIT = 25;

export type DriveMirroredRefMap = Map<string, DriveMirroredRef>;

export function invalidateDriveMirroredRefs(queryClient: QueryClient, driveId?: number): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: driveId === undefined ? drivesKeys.mirroredRefs() : drivesKeys.mirroredRefsForDrive(driveId),
  });
}

export const useDriveMirroredRefs = (driveId: number, paths: string[]) => {
  const sortedPaths = useMemo(() => [...new Set(paths)].sort(), [paths]);
  return useQuery({
    queryKey: drivesKeys.mirroredRefsQuery(driveId, sortedPaths),
    queryFn: () => listDriveMirroredRefs(driveId, { paths: sortedPaths }),
    select: (result): DriveMirroredRefMap => new Map(result.refs.map((ref) => [ref.path, ref])),
    enabled: sortedPaths.length > 0,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
};

export const useKeepDriveFiles = (driveId: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: DriveKeepBody) => keepDriveFiles(driveId, body),
    onSuccess: async () => {
      await Promise.all([
        invalidateDriveMirroredRefs(queryClient, driveId),
        queryClient.invalidateQueries({ queryKey: contentKeys.lists() }),
      ]);
    },
  });
};

export const useDriveLiveFind = (driveId: number, query: string, enabled: boolean) => {
  return useQuery({
    queryKey: drivesKeys.liveFind(driveId, query),
    queryFn: () => findLiveFiles(driveId, { query, limit: DRIVE_LIVE_FIND_LIMIT }),
    enabled,
    staleTime: 30_000,
  });
};

export const useDrives = (memberId: number, options?: { refetchInterval?: number }) => {
  return useQuery({
    queryKey: drivesKeys.list(memberId),
    queryFn: listDrives,
    enabled: memberId > 0,
    ...(options?.refetchInterval !== undefined ? { refetchInterval: options.refetchInterval } : {}),
  });
};

export const usePublishStatus = () => {
  return useQuery({
    queryKey: drivesKeys.publishStatus(),
    queryFn: getPublishStatus,
    refetchInterval: (query) =>
      query.state.data?.target?.inFlight ? PUBLISH_STATUS_POLL_IN_FLIGHT_MS : PUBLISH_STATUS_POLL_IDLE_MS,
  });
};

export const useSetPublishTarget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setPublishTarget,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const useRemovePublishTarget = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removePublishTarget,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const usePublishNow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: publishNow,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const useAvailableDrives = (memberId: number, enabled: boolean) => {
  return useQuery({
    queryKey: drivesKeys.available(memberId),
    queryFn: listAvailableDrives,
    enabled: enabled && memberId > 0,
  });
};

export const useBrowseDriveFolder = (driveId: number, parent: string | null, enabled: boolean) => {
  return useQuery({
    queryKey: drivesKeys.browse(driveId, parent),
    queryFn: () => browseDriveFolder(driveId, parent),
    enabled: enabled && driveId > 0,
  });
};

export const useAddDrive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addDrive,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const useRemoveDrive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeDrive,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const useAddDriveRoot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { driveId: number; body: DriveRootCreateBody }) => addDriveRoot(input.driveId, input.body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const useUpdateDriveRoot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { rootId: number; body: DriveRootUpdateBody }) => updateDriveRoot(input.rootId, input.body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const useRemoveDriveRoot = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeDriveRoot,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const useCurrentDriveSyncRun = (driveId: number) => {
  return useQuery({
    queryKey: drivesKeys.currentRun(driveId),
    queryFn: () => getCurrentDriveSyncRun(driveId),
    enabled: driveId > 0,
    refetchInterval: (query) => (query.state.data?.run ? 3000 : false),
    refetchIntervalInBackground: false,
  });
};

export const useDriveSyncRuns = (driveId: number, enabled: boolean) => {
  return useQuery({
    queryKey: drivesKeys.runs(driveId),
    queryFn: () => listDriveSyncRuns(driveId),
    enabled: enabled && driveId > 0,
  });
};

export const useSyncDriveNow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: syncDriveNow,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const useResyncDrive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: resyncDrive,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const useAnchorProposals = (rootId: number | undefined, enabled: boolean) => {
  return useQuery({
    queryKey: drivesKeys.proposals(rootId ?? 'all'),
    queryFn: () => listAnchorProposals(rootId),
    enabled,
  });
};

export const useDecideAnchorProposals = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: decideAnchorProposals,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
      await queryClient.invalidateQueries({ queryKey: ['content'] });
    },
  });
};

export const useReopenAnchorProposal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reopenAnchorProposal,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
    },
  });
};

export const useApplyDriveRoots = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: applyDriveRoots,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: drivesKeys.all });
      await queryClient.invalidateQueries({ queryKey: ['content'] });
    },
  });
};

export const useReadFileText = () => {
  return useMutation({
    mutationFn: (input: { contentId: number; startChar?: number; maxChars?: number }) =>
      readFileText(input.contentId, {
        ...(input.startChar !== undefined ? { startChar: input.startChar } : {}),
        ...(input.maxChars !== undefined ? { maxChars: input.maxChars } : {}),
      }),
  });
};

export const usePinReference = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pinReference,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['content'] });
    },
  });
};
